import api from '@/lib/api/axios'

/**
 * Mobilising standby users (MFA build plan, Stage 4). Mirrors backend lib/mobilisation.js
 * describeMobilisation - a phone number only ever arrives as its last three digits.
 */
export type MobilisationStatus = 'pending_approval' | 'approved' | 'rejected' | 'stood_down'
export type MemberRole = 'lead' | 'deputy' | 'member'
export type MemberStatus = 'waiting' | 'invited' | 'enrolled' | 'confirmed' | 'refused' | 'stood_down'
interface Person { id: number; name: string | null; email: string }

export interface MobilisationMember {
    id: number
    userId: number
    name: string | null
    email: string | null
    teamId: number
    teamName: string | null
    role: MemberRole
    status: MemberStatus
    phone: string | null
    hasNumber: boolean
    invitedAt: string | null
    enrolledAt: string | null
    confirmedAt: string | null
    sms: { status: 'queued' | 'sending' | 'sent' | 'failed' | 'cancelled'; attempts: number; sentAt: string | null; failed: boolean } | null
}
export interface Mobilisation {
    id: number
    recoverySetId: number
    status: MobilisationStatus
    reason: string
    codeHours: number
    requestedBy: Person | null
    requestedAt: string
    approvedBy: Person | null
    approvedAt: string | null
    stoodDownAt: string | null
    summary: { total: number; waiting: number; invited: number; enrolled: number; confirmed: number; refused: number; stoodDown: number; noNumber: number }
    members: MobilisationMember[]
}
export interface MobilisableTeam { id: number; name: string | null; users: Array<{ id: number; name: string | null; email: string; hasNumber: boolean }> }
export interface TeamChoice { teamId: number; leadUserId: number; deputyUserId?: number | null }
export interface MyTeam { mobilisationId: number; recoverySetId: number; teamId: number; teamName: string | null; role: MemberRole; members: MobilisationMember[] }

interface Wrapped<T> { success: boolean; data: T }
/** A call made on the sitting token, not a session (lib/api/axios.ts). */
const onSitting = (sittingToken: string) => ({ stepUpToken: sittingToken }) as Record<string, unknown>

export const mobilisationApi = {
    forSet: async (setId: number) =>
        (await api.get<Wrapped<{ mobilisation: Mobilisation | null; teams: MobilisableTeam[] }>>(`/api/mobilisations/recovery-set/${setId}`)).data.data,
    request: async (body: { setId: number; reason: string; teams: TeamChoice[] }) =>
        (await api.post<Wrapped<{ id: number; members: number; skipped: number[] }>>('/api/mobilisations', body)).data.data,
    approve: async (id: number) => (await api.post(`/api/mobilisations/${id}/approve`)).data,
    reject: async (id: number, reason: string) => (await api.post(`/api/mobilisations/${id}/reject`, { reason })).data,
    standDown: async (id: number, reason: string) => (await api.post(`/api/mobilisations/${id}/stand-down`, { reason })).data,
    confirm: async (memberId: number) => (await api.post(`/api/mobilisations/members/${memberId}/confirm`)).data,
    refuse: async (memberId: number, reason: string) => (await api.post(`/api/mobilisations/members/${memberId}/refuse`, { reason })).data,
    resend: async (memberId: number) => (await api.post(`/api/mobilisations/members/${memberId}/resend`)).data,
    mine: async () => (await api.get<Wrapped<{ teams: MyTeam[] }>>('/api/mobilisations/mine')).data.data.teams,
}

/** A mobilised standby user's one sitting: code, new password, device. It never ends in a session. */
export const sittingApi = {
    start: async (email: string, code: string) =>
        (await api.post<Wrapped<{ sittingToken: string; expiresInSeconds: number; passwordSet: boolean }>>('/auth/mobilise/start', { email, code })).data.data,
    password: async (token: string, newPassword: string) => (await api.post('/auth/mobilise/password', { newPassword }, onSitting(token))).data,
    totpBegin: async (token: string) =>
        (await api.post<Wrapped<{ secret: string; qrCode: string; setupTicket: string }>>('/auth/mobilise/totp/begin', {}, onSitting(token))).data.data,
    totpConfirm: async (token: string, body: { code: string; setupTicket: string; name?: string }) =>
        (await api.post<Wrapped<{ awaitingConfirmation: boolean }>>('/auth/mobilise/totp/confirm', body, onSitting(token))).data.data,
    passkeyBegin: async (token: string) => (await api.post<Wrapped<unknown>>('/auth/mobilise/passkey/begin', {}, onSitting(token))).data.data,
    passkeyComplete: async (token: string, body: { credential: unknown; name?: string }) =>
        (await api.post<Wrapped<{ awaitingConfirmation: boolean }>>('/auth/mobilise/passkey/complete', body, onSitting(token))).data.data,
}

export const mobilisationKeys = {
    forSet: (setId: number) => ['mobilisation', 'set', setId] as const,
    mine: ['mobilisation', 'mine'] as const,
}
